import { isBrowserAnalyticsBlocked, isAnalyticsPathExcluded } from "@/lib/analytics/cookies";
import { getSentryEnvironment, sentryOptions } from "@/lib/sentry";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

// Chrome's page translation swaps React's text nodes for <font> wrappers, so a
// later update removes or inserts beside a node that has moved and the game
// falls into the error page (facebook/react#11538). Skip those moves instead.
const removeChild = Node.prototype.removeChild;
Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
  if (child.parentNode !== this) return child;
  return removeChild.call(this, child) as T;
};
const insertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function <T extends Node>(
  this: Node,
  node: T,
  reference: Node | null,
): T {
  if (reference && reference.parentNode !== this) return node;
  return insertBefore.call(this, node, reference) as T;
};

const sentryEnvironment = getSentryEnvironment(window.location.hostname);

if (sentryEnvironment) {
  Sentry.init({
    ...sentryOptions(sentryEnvironment),
    // Errors only: no session pings and no trace headers on our requests.
    integrations: (defaults) =>
      defaults.filter(
        ({ name }) => name !== "BrowserSession" && name !== "BrowserTracing",
      ),
  });
}

const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;

if (key) {
  posthog.init(key, {
    api_host: "/ingest",
    ui_host:
      process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || "https://us.posthog.com",
    request_batching: true,
    disable_external_dependency_loading: false,
    disable_surveys: true,
    advanced_disable_flags: false,
    autocapture: {
      dom_event_allowlist: ["click", "change", "submit"],
      element_allowlist: ["a", "button", "form", "input", "select", "textarea"],
      element_attribute_ignorelist: ["value", "placeholder"],
      capture_copied_text: false,
    },
    capture_pageview: false,
    capture_pageleave: true,
    capture_performance: true,
    capture_heatmaps: true,
    capture_dead_clicks: true,
    capture_exceptions: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-private], .ph-no-capture",
      blockSelector: "[data-ph-block]",
    },
    person_profiles: "identified_only",
    respect_dnt: true,
    before_send(event) {
      const pathname = window.location.pathname;
      if (
        isBrowserAnalyticsBlocked() || isAnalyticsPathExcluded(pathname)
      ) {
        return null;
      }
      return event;
    },
  });
}
